<template>
  <div class="form-wizard-step summary">
    <div v-if="results">
      <section class="roof-section">
        <div class="container">
          <h1 class="page-section__title">
            <span class="page-section__title__background">{{ results.tile.title }}</span>
            <span class="page-section__title__main">{{ results.tile.color }}</span>
          </h1>
          <div class="roof-summary">
            <div class="row">
              <div class="col-md-4 order-1 order-md-0 mb-4 mb-md-0">
                <ul class="roof-summary__list">
                  <li>
                    <span class="roof-summary__label">{{ t('general.surface') }}</span>
                    <strong>{{ results.surface }}</strong>
                  </li>
                  <li>
                    <span class="roof-summary__label">{{ t('steps.summaryStep.terms.warranty') }}</span>
                    <strong>{{ results.tile.warranty }} {{ t('general.year') }}</strong>
                  </li>
                  <li>
                    <span class="roof-summary__label">{{ t('steps.summaryStep.terms.gross') }}</span>
                    <strong :class="{ 'has-sale': results.tile?.sale_gross }">{{ results.tile.gross }}</strong>
                    <strong v-if="results.tile?.sale_gross" class="sale">{{ results.tile?.sale_gross }}</strong>
                  </li>
                </ul>
                <button @click="changeTileShape" class="btn btn-primary no-print">
                  {{ t('steps.summaryStep.buttons.changeTileShape') }}
                </button>
              </div>
              <div class="col-md-4 order-0 order-md-1">
                <div class="roof-summary__image text-center">
                  <img :src="results.tile.image" :alt="results.tile.title" />
                </div>
              </div>
              <div class="col-md-4 order-2">
                <ul class="roof-summary__list">
                  <li>
                    <span class="roof-summary__label">{{ t('steps.summaryStep.terms.roofType') }}</span>
                    <strong>{{ results.roof_type.title }}</strong>
                  </li>
                  <li v-for="(side, index) in results.sides" :key="index">
                    <span class="roof-summary__label">
                      {{ t(`general.roofSides.${index}`) }} ({{ getRoofSideLabel(index) }})
                    </span>
                    <strong>{{ side }} m</strong>
                  </li>
                  <li>
                    <span class="roof-summary__label">{{ t('steps.summaryStep.terms.degree') }}</span>
                    <strong>{{ results.degree }}°</strong>
                  </li>
                </ul>
                <button @click="initNewCalculation" class="btn btn-danger no-print">
                  {{ t('steps.summaryStep.buttons.newCalculation') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div class="container">
        <RoofPriceBanner v-if="results?.price" :results="results" />
      </div>

      <template v-if="hasTudastar">
        <TudastarBanner v-if="results" :results="results" />
      </template>

      <div class="container">
        <div class="row print-row wood-materials-and-generon-section">
          <div class="col-lg-6">
            <template v-if="hasWoodMaterials">
              <WoodMaterialsPriceBanner :results="results" />
            </template>
          </div>
          <div class="col-lg-6">
            <template v-if="hasGeneron && generonInterested">
              <GeneronCalculator :results="results" />
            </template>
          </div>
        </div>
      </div>

      <div class="" style="background: #ededed; padding-top: 32px">
        <div v-if="results && displayGallery">
          <h3 class="" style="margin-bottom: 32px; text-align: center">
            <span class="">{{ results.tile.title }}</span>
            <span class="">{{ t('steps.summaryStep.sections.gallery.title') }}</span>
          </h3>
        </div>
        <template v-if="displayGallery">
          <Gallery />
        </template>

        <div class="container">
          <template v-if="hasCetelem">
            <CetelemCalculatorBanner />
          </template>

          <template v-if="hasEnvirosense">
            <EnvirosenseBanner :results="results" />
          </template>
          <!-- <GeneronCarousel />-->
          <!-- <GeneronInfoTable />-->

          <section v-if="results.banners && showBanners" class="banners-section no-print">
            <div class="d-flex flex-wrap justify-content-center">
              <DefaultBanner v-for="banner in results.banners" :key="banner.id" :banner="banner" />
            </div>
          </section>
        </div>
      </div>

      <template v-if="hasTetotepitek">
        <TetotepitekBanner />
      </template>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue'
import { useI18n } from 'vue-i18n'

import useFormState from '@/hooks/formState'
import useGtmEvent from '@/hooks/gtm'
import { LANGUAGE_CONFIG } from '@/services/language'

import DefaultBanner from '@/components/Banners/DefaultBanner.vue'
// import MaterialRequirementsBanner from '@/components/Banners/MaterialRequirementsBanner.vue'
import WoodMaterialsPriceBanner from '@/components/Banners/WoodMaterialsPriceBanner.vue'
import RoofPriceBanner from '@/components/Banners/RoofPriceBanner.vue'
// import GeneronRoofPriceBanner from '@/components/Banners/GeneronRoofPriceBanner.vue'
import EnvirosenseBanner from '@/components/Banners/EnvirosenseBanner.vue'
import TetotepitekBanner from '@/components/Banners/TetotepitekBanner'
import TudastarBanner from '@/components/Banners/TudastarBanner'
import CetelemCalculatorBanner from '@/components/Banners/CetelemCalculatorBanner'
import GeneronCalculator from '@/components/Calculator/GeneronCalculator.vue'
import Gallery from '@/components/Global/Gallery.vue'
import useBreakpoint from '@/hooks/useBreakpoint'
// import GeneronBanner from '@/components/Banners/GeneronBanner.vue'
// import GeneronCarousel from '@/components/Generon/GeneronCarousel.vue'
// import GeneronInfoTable from '@/components/Generon/GeneronInfoTable.vue'

export default defineComponent({
  name: 'SummaryStep',
  components: {
    Gallery,
    GeneronCalculator,
    DefaultBanner,
    // MaterialRequirementsBanner,
    WoodMaterialsPriceBanner,
    RoofPriceBanner,
    // GeneronRoofPriceBanner,
    EnvirosenseBanner,
    TetotepitekBanner,
    TudastarBanner,
    CetelemCalculatorBanner
    // GeneronBanner,
    // GeneronCarousel,
    // GeneronInfoTable,
  },
  setup() {
    const { formState, updateFormState } = useFormState()
    const { trackEvent } = useGtmEvent()
    const { t } = useI18n()
    const { isMobile } = useBreakpoint()

    const currentLanguage = localStorage.getItem('language') || process.env.VUE_APP_DEFAULT_LOCALE
    const showBanners = LANGUAGE_CONFIG[currentLanguage].showBanners
    const hasEnvirosense = LANGUAGE_CONFIG[currentLanguage].hasEnvirosense
    const hasTetotepitek = LANGUAGE_CONFIG[currentLanguage].hasTetotepitek
    const hasTudastar = LANGUAGE_CONFIG[currentLanguage].hasTudastar
    const hasWoodMaterials = LANGUAGE_CONFIG[currentLanguage].hasWoodMaterials
    const hasCetelem = LANGUAGE_CONFIG[currentLanguage].hasCetelem
    const hasGeneron = LANGUAGE_CONFIG[currentLanguage].hasGeneron
    const hasGallery = LANGUAGE_CONFIG[currentLanguage].hasGallery

    const displayGallery = computed(() => {
      return !isMobile.value && hasGallery
    })

    function getRoofSideLabel(side) {
      return side.split('_')[1].toUpperCase()
    }

    function initNewCalculation() {
      trackEvent({
        category: 'új kalkuláció',
        action: 'kattintas'
      })
      window.open(window.location.origin, '_blank')
    }

    function changeTileShape() {
      const currentLanguage = localStorage.getItem('language') || process.env.VUE_APP_DEFAULT_LOCALE
      const TILE_SHAPE_STEP = LANGUAGE_CONFIG[currentLanguage].steps.findIndex(e => e === 'TileShape')

      trackEvent({
        category: 'cserép módosítása',
        action: 'kattintas'
      })

      updateFormState({ results: null, tile: null, tileShape: null })
      localStorage.setItem('tc_form', JSON.stringify({ ...formState }))

      window.open(`${window.location.href}?step=${TILE_SHAPE_STEP}`, '_blank')
    }

    return {
      results: formState.results,
      getRoofSideLabel,
      initNewCalculation,
      changeTileShape,
      t,
      showBanners,
      hasEnvirosense,
      hasTetotepitek,
      hasWoodMaterials,
      hasTudastar,
      hasCetelem,
      hasGeneron,
      hasGallery,
      formState,
      displayGallery,
      generonInterested: formState.tileShape.id === 1
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/assets/style/base/variables' as *;

.banners-section {
  padding: 40px 0;

  @media (min-width: 992px) {
    padding: 80px 0 50px;
  }
}

.roof-section {
  padding-top: 40px;
}

.roof-summary {
  margin-top: 20px;

  .btn {
    @media (max-width: 765px) {
      display: block;
      width: 100%;
    }
  }

  &__list {
    padding: 0;
    margin-bottom: 20px;

    li {
      display: flex;
      padding: 8px 0;

      &:not(:last-child) {
        border-bottom: 2px solid $white-two;
      }

      .roof-summary__label {
        width: 50%;
      }

      strong {
        font-family: $font-family-bold;

        &.sale {
          color: $lipstick;
          margin-left: 0.5rem;
        }

        &.has-sale {
          text-decoration: line-through;
          text-decoration-thickness: 0;
        }
      }
    }
  }

  &__image {
    @media print {
      img {
        max-height: 350px !important;
      }
    }
  }
}

.wood-materials-and-generon-section {
  margin-bottom: 24px;

  @media screen and (min-width: 992px) {
    margin-bottom: 96px;
  }
}
</style>
